import { useCallback, useEffect, useMemo, useState } from 'react';

import type { OptionList, OptionListSummary } from '@src/models/interfaces/optionList.interface';
import OptionListService from '@src/services/OptionListService';

/** How long fetched list names are reused before a mount fetches them again. */
export const OPTION_LISTS_CACHE_MS = 60_000;

interface CachedLists {
  fetchedAt: number;
  request: Promise<OptionListSummary[]>;
}

/**
 * Shared per organization so a form with several list fields (one picker each) fetches the list
 * names once. A failed request is dropped so the next mount retries.
 */
const listsCache = new Map<string, CachedLists>();

const cacheKey = (organizationId: string, divisionIds?: string[]) =>
  `${organizationId}|${divisionIds ? [...divisionIds].sort().join(',') : '*'}`;

const fetchLists = (
  service: OptionListService,
  organizationId: string,
  divisionIds: string[] | undefined,
  force: boolean,
) => {
  const key = cacheKey(organizationId, divisionIds);
  const cached = listsCache.get(key);
  if (!force && cached && Date.now() - cached.fetchedAt < OPTION_LISTS_CACHE_MS) {
    return cached.request;
  }
  const request = service.list(organizationId, divisionIds);
  const entry = { fetchedAt: Date.now(), request };
  listsCache.set(key, entry);
  request.catch(() => {
    if (listsCache.get(key) === entry) listsCache.delete(key);
  });
  return request;
};

/** Drops the cached names of one organization (after creating or editing a list), or all. */
export const clearOptionListsCache = (organizationId?: string) => {
  if (!organizationId) {
    listsCache.clear();
    return;
  }
  Array.from(listsCache.keys())
    .filter(key => key.startsWith(`${organizationId}|`))
    .forEach(key => listsCache.delete(key));
};

/**
 * Organization option lists (names and counts), for the "Listas" screen and the form builder.
 * Pass `divisionIds` (the form's divisions) to get only the lists that form may use; see
 * {@link OptionListService.list}. `refresh` fetches again and updates the shared copy, so pickers
 * mounted later see the change.
 */
export const useOptionLists = (organizationId: string | undefined, divisionIds?: string[]) => {
  // Callers pass a fresh array each render; key the effect on its contents instead.
  const scopeKey = divisionIds ? [...divisionIds].sort().join(',') : undefined;
  const scope = useMemo(() => (scopeKey === undefined ? undefined : scopeKey.split(',').filter(Boolean)), [scopeKey]);
  const service = useMemo(() => new OptionListService(), []);
  const [lists, setLists] = useState<OptionListSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!organizationId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchLists(service, organizationId, scope, refreshKey > 0)
      .then(result => {
        if (!cancelled) setLists(result);
      })
      .catch(err => {
        if (!cancelled) setError(err as Error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, refreshKey, scope, service]);

  const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

  return { lists, loading, error, refresh, service };
};

/**
 * One option list with its items. Pass `divisionId` for screens used by division users: they read
 * the division mirror, which exists while a form of that division uses the list. A missing list
 * is reported as an error, so it never looks like a list with no items.
 */
export const useOptionList = (
  organizationId: string | undefined,
  listId: string | undefined,
  divisionId?: string,
) => {
  const service = useMemo(() => new OptionListService(), []);
  const [list, setList] = useState<OptionList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!organizationId || !listId) {
      setList(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    service
      .get(organizationId, listId, divisionId)
      .then(result => {
        if (cancelled) return;
        setList(result);
        if (!result) setError(new Error('not-found'));
      })
      .catch(err => {
        if (!cancelled) setError(err as Error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [divisionId, listId, organizationId, service]);

  return { list, loading, error };
};
